import { clsx } from 'clsx'
import { type ComponentProps, type ComponentType, type FC } from 'react'

import { CmsComponentStickyBannerTopHeaderSchemaEnum } from '@redteclab/api/clients/content-management-system'
import { IconPrescription } from '@redteclab/icons'

import { useExpaContext } from '../../../../expa'
import { useGetExperimentForTenant } from '../../../../experiments/hooks'
import { EXPERIMENT_HEADER_STICKY_TOGGLE } from '../../../../experiments/model/EXPERIMENT_NAME_PER_TENANT'
import { experimentAccessorNonDefault } from '../../../../experiments/model/experimentAccessor'
import { IconPrimaryArrow } from '../../../../icons'
import { useHeaderDataContext } from '../../headerConfigContext'

const icons: Record<string, ComponentType<ComponentProps<'svg'>>> = {
  prescription: IconPrescription,
  primaryArrow: IconPrimaryArrow,
}

const getMobileOrDefaultText = ({
  mainText,
  text_mobile,
}: {
  mainText: string
  text_mobile: string | undefined
}): string => {
  // if mobile text is not defined in CMS, it will be empty string
  if (text_mobile) {
    return text_mobile
  }

  return mainText
}

// eslint-disable-next-line max-statements -- needed for the experiment
export const TopHeaderStickyBanner: FC = () => {
  const headerData = useHeaderDataContext()
  const expa = useExpaContext()

  const headerStickyToggleExperiment = useGetExperimentForTenant(
    EXPERIMENT_HEADER_STICKY_TOGGLE,
  )

  if (!headerData.cmsMenu.items) {
    return null
  }

  const stickyBannerTopHeader = headerData.cmsMenu.items.find(
    (item) =>
      item.schema ===
      CmsComponentStickyBannerTopHeaderSchemaEnum.CmsComponentStickyBannerTopHeader,
  )

  if (!stickyBannerTopHeader || stickyBannerTopHeader.hidden === true) {
    return null
  }

  const {
    cleansite,
    icon,
    link,
    text: mainText,
    text_mobile,
  } = stickyBannerTopHeader
  /**
   * usecase e.g. for CH - when gglp hide prescription ribbon
   * https://jira.shop-apotheke.com/browse/WSAWA-5470
   */
  if (expa && cleansite?.includes(expa)) {
    return null
  }

  const Icon = icon && icons[icon]

  let text = (
    <>
      <span
        className="hidden tablet:inline"
        // eslint-disable-next-line react/no-danger -- no other way around
        dangerouslySetInnerHTML={{
          __html: mainText,
        }}
      />
      <span
        className="inline tablet:hidden"
        // eslint-disable-next-line react/no-danger -- no other way around
        dangerouslySetInnerHTML={{
          __html: getMobileOrDefaultText({ mainText, text_mobile }),
        }}
      />
    </>
  )

  if (link) {
    text = (
      <a className="after:absolute after:inset-0" href={link}>
        {text}
      </a>
    )
  }

  return (
    <div
      className={clsx(
        'top-header-sticky-banner',
        experimentAccessorNonDefault(headerStickyToggleExperiment) &&
          'relative top-0',
      )}
    >
      <div className="container flex items-start">
        {Icon ? <Icon className="-my-0.5 mr-1 size-6 shrink-0" /> : null}
        {text}
      </div>
    </div>
  )
}
